import { http, getHttpErrorMessage } from '@/helpers/http'
import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { useUserStore } from '@/stores/user'
import { useCreatorStore } from '@/stores/creator'
import { useProjectsStore } from '@/stores/projects'
import { usePrintBooksStore } from './print-books'
import { useSnackbarStore } from '@/stores/snackbar'
import { useRoute, useRouter } from 'vue-router'
import { reactive, ref } from 'vue'
import { DRAFT_TABS } from '@/types/drafts'
import type {
  TDraft,
  TDraftImportType,
  TDraftListing,
  TDraftTab,
  TDraftSaveResponse,
  TDraftStatus,
  TDraftsPagination,
  TDraftType,
  TSortOptions,
  TUserSortOptions,
  TDraftFlags,
} from '@/types/drafts'
import { track } from '@/helpers/mixpanelDes'
import { createDebounceFn } from '@/helpers/debounce'
import { detectRTL } from '@/helpers/rtl'
import type { TPrintBook } from '@/types/print-books'

export const DRAFT_PAGE_SIZE = 30

/** The routes the cached draft listings stay alive across. */
export const DRAFT_LISTING_ROUTES = ['drafts', 'projects']

export const useDraftsStore = defineStore('drafts', () => {
  const labels: { [key: string]: string } = {
    // types
    'from-blog': 'Import from Blog post or URL',
    'from-docx': 'Import from Word DOCX',
    'from-gdoc': 'Import from Google Doc',
    'from-video': 'Import from Video',
    'from-template': 'Start from template',
    'merge-drafts': 'Merge docs',
    'pdf-to-flipbook': 'PDF',
    'from-podcast': 'Import from podcast',
    'from-pdf': 'Import from PDF',
    'from-pptx': 'Import from PowerPoint PPTX',
    'from-gslides': 'Import from Google Slides',
    'from-scratch': 'Start from scratch',
    'from-wordgenie': 'Generate with Wordgenie',
    'from-file': 'Upload file/Insert link',

    //subtypes
    'audio': 'Import from podcast',
    'youtube': 'Import from YouTube',
    'vimeo': 'Import from Vimeo',
    'dropbox': 'Import a video on Dropbox',
    'wistia': 'Import a video on Wistia',
    'video': 'Import from video',
  }

  function newListing(type: TDraftTab): TDraftListing {
    return { type, rows: [], total: null, page: 1, sortBy: '', query: '', loaded: false, loading: false }
  }

  const listings = reactive(new Map<TDraftTab, TDraftListing>(DRAFT_TABS.map((type) => [type, newListing(type)])))
  const pendingLoads = new Map<TDraftTab, Promise<void>>()
  const pendingTotals = new Map<TDraftTab, Promise<void>>()

  const draft = ref<Partial<TDraft> | null>(null)
  const draftFlags = ref<TDraftFlags | null>(null)
  const draftsIdsWithoutThumbnails = ref<number[]>([])
  const typeTotals = ref<Partial<Record<TDraftTab, number>>>({})
  const draftsSortBy = ref<string>('newest')
  const sortOptions = ref<TUserSortOptions>({})
  let sortResolvedFor: number | null = null
  const user = useUserStore()
  const router = useRouter()
  const creator = useCreatorStore()
  const printBook = usePrintBooksStore()
  const route = useRoute()
  const generateRunning = ref<boolean>(false)
  const generateTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const debouncedGenerate = createDebounceFn(
    ({ draftId, draftType }: { draftId: number; draftType?: TDraftType | null }) =>
      generateThumbnail(draftId, draftType),
    30000,
  )
  let pendingThumbnailDraftId: number | null = null

  router.afterEach((to, from) => {
    if (to.name !== from.name) {
      flushPendingThumbnail()
    }
  })

  function listing(type: TDraftTab): TDraftListing {
    return listings.get(type)!
  }

  /** A listing that lives outside the shared map, for a caller whose filtering must not leak into a tab. */
  function createListing(type: TDraftTab): TDraftListing {
    return reactive(newListing(type))
  }

  type TDraftLanguageDetectionResponse = {
    result: boolean
    detected: string | null
    languages?: Record<string, number>
  }

  function getLabel(type: string): string {
    return labels[type] || ''
  }

  const typeLabels: Partial<Record<TDraftType, string>> = {
    Standard: 'manuscript',
    PrintBook: 'print book',
    KindleBook: 'kindle book',
    AudioBook: 'audiobook',
    Audio: 'transcription',
    TemiJob: 'transcription',
  }

  function getTypeLabel(type: TDraftType | string | undefined | null): string {
    return (type && typeLabels[type as TDraftType]) || 'item'
  }

  // mirrors Builder\Drafts\Services\DraftCatalog::TYPE_MAP
  const typeTabs: Record<TDraftType, TDraftTab> = {
    Standard: 'standard',
    PrintBook: 'printbooks',
    KindleBook: 'kindlebooks',
    AudioBook: 'audiobooks',
    Audio: 'transcriptions',
    TemiJob: 'transcriptions',
  }

  function decrementTypeTotals(type: TDraftType): void {
    const affected: TDraftTab[] = [typeTabs[type], 'all']
    affected.forEach((key) => {
      if (typeTotals.value[key]) {
        typeTotals.value[key]--
      }
    })
  }

  async function checkThumbnail() {
    const id = draftsIdsWithoutThumbnails.value.length > 0 ? draftsIdsWithoutThumbnails.value[0] : null
    if (!id) {
      return
    }

    if (draftsIdsWithoutThumbnails.value.length > 0) {
      draftsIdsWithoutThumbnails.value.shift()
    }

    if (DRAFT_LISTING_ROUTES.includes(String(router.currentRoute.value.name)) && generateRunning.value === true) {
      await generateThumbnail(id)
      generateTimeout.value = setTimeout(() => {
        checkThumbnail()
      }, 1000)
    } else {
      generateRunning.value = false
    }
  }

  function needsThumbnail(item: TDraft): boolean {
    if (item.thumbnail) {
      return false
    }
    if (item.type === 'TemiJob') {
      return item.status === 'Transcribed'
    }
    return ['Standard', 'Audio', 'AudioBook', 'PrintBook', 'KindleBook'].includes(item.type)
  }

  function queueThumbnails(entry: TDraftListing, options: { restart: boolean }): void {
    draftsIdsWithoutThumbnails.value = entry.rows.filter(needsThumbnail).map((item) => item.id)
    if (options.restart) {
      generateRunning.value = true
    }
    if (generateTimeout.value) {
      clearTimeout(generateTimeout.value)
    }
    checkThumbnail()
  }

  function readSortOptions(): void {
    sortOptions.value = useStorage('sortOptions', {} as TUserSortOptions).value
  }

  /**
   * Take the persisted sort into `draftsSortBy` once per user. Re-reading it on every fetch would
   * race the debounced localStorage write behind `setSort`.
   */
  function resolveSortBy(): void {
    if (sortResolvedFor === user.user.id) {
      return
    }
    readSortOptions()
    draftsSortBy.value = sortOptions.value[user.user.id]?.drafts || 'newest'
    sortResolvedFor = user.user.id
  }

  function setSort(sortBy: string): void {
    readSortOptions()
    draftsSortBy.value = sortBy
    if (!sortOptions.value[user.user.id]) {
      sortOptions.value[user.user.id] = <TSortOptions>{}
    }
    sortOptions.value[user.user.id]!.drafts = sortBy
    sortResolvedFor = user.user.id
    invalidateAll()
  }

  async function fetchInto(
    entry: TDraftListing,
    { page = 1, append = false, query = '', limit = DRAFT_PAGE_SIZE } = {},
  ): Promise<TDraftsPagination | null> {
    entry.loading = true
    try {
      const res = await http.post<{
        results: TDraft[]
        pagination: TDraftsPagination
      }>(`/get_drafts/${page}/${entry.type}/${draftsSortBy.value}/${limit}`, {
        query: query,
      })

      printBook.validateStoredSteps()

      const rows = res?.results ?? []
      entry.rows = append ? entry.rows.concat(rows) : rows
      entry.page = page
      entry.query = query
      entry.sortBy = draftsSortBy.value
      if (query) {
        entry.loaded = false
      }

      return res?.pagination ?? null
    } finally {
      entry.loading = false
    }
  }

  /** Fetch a tab's first page unless it is already held for the current sort. */
  async function ensure(type: TDraftTab): Promise<void> {
    resolveSortBy()
    const entry = listing(type)
    if (entry.loaded && entry.sortBy === draftsSortBy.value) {
      queueThumbnails(entry, { restart: true })
      return
    }

    const pending = pendingLoads.get(type)
    if (pending) {
      return await pending
    }

    const load = (async () => {
      const pagination = await fetchInto(entry, { page: 1 })
      entry.loaded = true
      if (pagination) {
        entry.total = pagination.total
        typeTotals.value[type] = pagination.total
      }
      queueThumbnails(entry, { restart: true })
    })().finally(() => {
      pendingLoads.delete(type)
    })

    pendingLoads.set(type, load)
    return await load
  }

  /** A filtered fetch: it fills the given listing and never claims to be that tab's full first page. */
  async function search(entry: TDraftListing, query: string, limit: number): Promise<void> {
    resolveSortBy()
    await fetchInto(entry, { page: 1, query, limit })
    queueThumbnails(entry, { restart: true })
  }

  function invalidate(type: TDraftTab): void {
    listing(type).loaded = false
  }

  function invalidateAll(): void {
    listings.forEach((entry) => {
      entry.loaded = false
    })
  }

  /** Refetch a tab now, but only if it is already held; otherwise the next `ensure` fetches it. */
  async function reload(type: TDraftTab): Promise<void> {
    if (!listing(type).loaded) {
      return
    }
    invalidate(type)
    await ensure(type)
  }

  /** Append a tab's next page. Only the shared, unfiltered tab listings paginate. */
  async function fetchNext(type: TDraftTab): Promise<void> {
    const entry = listing(type)
    if (entry.total !== null && entry.rows.length >= entry.total) {
      return
    }

    const pagination = await fetchInto(entry, { page: entry.page + 1, append: true })

    if (pagination) {
      entry.total = pagination.total
      typeTotals.value[entry.type] = pagination.total
    }
    queueThumbnails(entry, { restart: false })
  }

  function findDraft(id: number): TDraft | undefined {
    for (const entry of listings.values()) {
      const row = entry.rows.find((item) => item.id === id)
      if (row) {
        return row
      }
    }
    return undefined
  }

  function patchDraft(id: number, patch: Partial<TDraft>): void {
    listings.forEach((entry) => {
      const row = entry.rows.find((item) => item.id === id)
      if (row) {
        Object.assign(row, patch)
      }
    })
  }

  function spliceDraft(id: number): void {
    listings.forEach((entry) => {
      const index = entry.rows.findIndex((item) => item.id === id)
      if (index < 0) {
        return
      }
      entry.rows.splice(index, 1)
      if (entry.total !== null) {
        entry.total--
      }
    })
  }

  function dequeueThumbnail(id: number): void {
    const index = draftsIdsWithoutThumbnails.value.indexOf(id)
    if (index >= 0) {
      draftsIdsWithoutThumbnails.value.splice(index, 1)
    }
  }

  function flushPendingThumbnail() {
    debouncedGenerate.flush().catch(() => undefined)
  }

  function cancelPendingThumbnail(draftIds: number[]) {
    if (pendingThumbnailDraftId !== null && draftIds.includes(pendingThumbnailDraftId)) {
      debouncedGenerate.cancel()
    }
  }

  function debouncedGenerateThumbnail(draftId: number, draftType?: TDraftType | null) {
    if (debouncedGenerate.isPending() && pendingThumbnailDraftId !== draftId) {
      flushPendingThumbnail()
    }

    pendingThumbnailDraftId = draftId
    debouncedGenerate({ draftId, draftType })
  }

  function getDraftTypeForThumbnail(draftId: number, draftType?: TDraftType | null): TDraftType | null {
    if (draftType) {
      return draftType
    }

    if (draft.value?.id === draftId) {
      return draft.value.type ?? null
    }

    return findDraft(draftId)?.type ?? null
  }

  async function linkCoverToKindleBookDraft(draftId: number, coverId: number): Promise<boolean> {
    try {
      const res = await http.post<{ status: string }>('/linkCoverToKindleBookDraft', { draftId, coverId })
      return res?.status === 'success'
    } catch {
      return false
    }
  }

  async function saveKindleBookSettings(draftId: number, description: string, hideCover: boolean): Promise<boolean> {
    try {
      const res = await http.post<{ status: string }>('/saveKindleBookSettings', { draftId, description, hideCover })
      return res?.status === 'success'
    } catch {
      return false
    }
  }

  async function generateThumbnail(draftId: number, draftType?: TDraftType | null) {
    if (route.name === 'cover-editor') {
      return
    }

    const resolvedDraftType = getDraftTypeForThumbnail(draftId, draftType)

    if (resolvedDraftType === 'PrintBook') {
      const generated = await printBook.generateThumbnail(draftId)
      if (generated) {
        patchDraft(draftId, { thumbnail: generated.thumbnail, thumbnail_src: generated.thumbnail_src })
      }
      return
    }

    const result = await http.get<{
      thumbnail: string
      thumbnail_src?: string | null
    }>(`/generate_draft_thumbnail/${draftId}`)
    if (result) {
      patchDraft(draftId, { thumbnail: result.thumbnail, thumbnail_src: result.thumbnail_src ?? null })
    }
  }

  async function storeUnsavedContent(id: number, content: string): Promise<void> {
    await http.post('/storeUnsavedContent', { id, content })
  }

  // Stop polling drafts older than this: a job that has not settled in three days is stuck/broken
  // (its background download would already have been failed by the 48h cleanup cron), so polling it
  // would just hammer the transcription provider forever.
  const TRANSCRIPTION_POLL_MAX_AGE_MS = 72 * 60 * 60 * 1000

  function isTranscriptionPending(item: TDraft): boolean {
    return (
      (item.type === 'TemiJob' || item.type === 'Audio') &&
      (item.status === 'Downloading' || item.status === 'In progress')
    )
  }

  function isTranscriptionPollable(item: TDraft): boolean {
    if (!isTranscriptionPending(item)) {
      return false
    }
    const createdAt = item.created_at ? new Date(item.created_at.replace(' ', 'T')).getTime() : 0
    if (!createdAt) {
      return false
    }
    return Date.now() - createdAt < TRANSCRIPTION_POLL_MAX_AGE_MS
  }

  /**
   * Refresh the status of any recently-created transcription drafts that are still downloading or
   * transcribing, updating them in place. Returns whether any are still pending so callers can
   * keep polling until everything settles.
   */
  async function pollTranscriptionStatuses(type: TDraftTab): Promise<boolean> {
    const visible = listing(type).rows
    const pending = visible.filter(isTranscriptionPollable)
    if (pending.length === 0) {
      return false
    }
    await Promise.all(
      pending.map(async (item) => {
        try {
          const res = await http.post<{ status: string; draft_status?: TDraftStatus }>(
            '/refresh_transcription_status',
            { id: item.id },
          )
          if (res?.status !== 'success' || !res.draft_status) {
            return
          }
          const wasPending = isTranscriptionPending(item)
          patchDraft(item.id, { status: res.draft_status })
          if (res.draft_status === 'Transcribed' && wasPending && !item.thumbnail) {
            generateThumbnail(item.id, item.type)
          }
        } catch {
          // ignore transient polling errors; the next tick will retry
        }
      }),
    )
    return visible.some(isTranscriptionPollable)
  }

  function updateImportTypeString(importType: string, newType: string) {
    let importTypeObj = { book_type: newType }
    if (importType) {
      try {
        const parsed = JSON.parse(importType)
        importTypeObj = { ...parsed, book_type: newType }
      } catch (e) {
        console.log('Error parsing importType string:', e)
      }
    }
    return JSON.stringify(importTypeObj)
  }

  function isCurrentDraftKindleBook(): boolean {
    return (
      draft.value?.type === 'KindleBook' ||
      route.name === 'draft-kindle-book' ||
      ['kindle', 'kindle-book'].some((p) => route.path.includes(p))
    )
  }

  function isCurrentDraftPrintBook(): boolean {
    return (
      draft.value?.type === 'PrintBook' ||
      route.name === 'draft-print-book' ||
      ['printbook', 'print-book'].some((p) => route.path.includes(p)) ||
      (creator.isWordgeniePrintbookImport && creator.source?.name === 'from-wordgenie')
    )
  }

  async function save(
    id: number | null,
    title: string,
    content: string,
    url: string,
    importType?: TDraftImportType | string | null,
    importSource?: string | null,
    tags?: number[],
    type?: TDraftType | null,
    genThumbImmediately?: boolean,
    printBook?: TPrintBook | null,
    pagesCount?: number | null,
    newKindleBookDraft?: boolean | null,
  ) {
    if (id && tags) {
      //do not save tags over and over again
      tags = undefined
    }

    const isEnhancedVersion = id ? draft.value?.enhanced_version || false : false //can't enhance a draft not saved yet
    if (isEnhancedVersion && (!content || content.trim() === '')) {
      throw new Error('Cannot save enhanced version with empty content')
    }

    const currentDraftType = draft.value?.type ?? type ?? null
    const isPrintBook = currentDraftType === 'PrintBook' || isCurrentDraftPrintBook()
    if (isPrintBook && importType && typeof importType === 'string') {
      importType = updateImportTypeString(importType, 'printbook')
    }

    const res = await http.post<TDraftSaveResponse>(`/save_draft`, {
      articleTitle: title,
      articleUrl: url,
      content,
      draftName: title,
      rtl: detectRTL(content),
      ...(validSource(importType ?? undefined) && { importType: validSource(importType ?? undefined) }),
      ...(id && { id }),
      ...(importSource && { importSource }),
      ...(tags && { tags }),
      ...(type && { type }),
      ...(printBook && { printBook }),
      enhancedVersion: isEnhancedVersion,
      isPrintBook,
      ...(pagesCount && { pagesCount }),
      ...(newKindleBookDraft && { newKindleBookDraft }),
    })
    if (res.status === 'success') {
      if (id === null || newKindleBookDraft) {
        invalidateAll()
      }
      if (genThumbImmediately) {
        await generateThumbnail(res.id, currentDraftType).catch(() => undefined)
      } else {
        debouncedGenerateThumbnail(res.id, currentDraftType)
      }
      //actualize pages count in store
      if (isPrintBook && pagesCount && draft.value?.print_book?.id) {
        draft.value.print_book.pages = pagesCount
      }
      return res.id
    }
    throw res.message ?? 'Doc saving error'
  }

  function validSource(importType?: TDraftImportType | string): TDraftImportType | null {
    if (!importType) return null

    if (typeof importType === 'string') {
      try {
        const type = <TDraftImportType>JSON.parse(importType)
        if (!type.type) return null
        return type
      } catch {
        return null
      }
    }

    return importType
  }

  async function rename(draftId: number, draftName: string): Promise<boolean> {
    try {
      await http.post('/rename_draft', { draftId, draftName })
      track('draft-renamed', {
        name: draftName,
      })
      return true
    } catch (error) {
      track('draft-rename-failed', {
        name: draftName,
        error: getHttpErrorMessage(error, 'Could not rename this document'),
      })
      return false
    }
  }

  async function duplicate(draft: TDraft, name: string, duplicateCover: boolean = false) {
    const res = await http.post<TDraft>('/duplicate_draft/', {
      id: draft.id,
      name,
      duplicateCover,
    })
    if (!res) {
      track('draft-duplicate-failed', {
        name: draft.name,
      })
      throw new Error(`Failed to duplicate draft`)
    }
    track('draft-duplicated', {
      name: draft.name,
    })
    await reload(typeTabs[draft.type])
    return res
  }

  async function deleteDraft(draftId: number) {
    return await http.post('/delete_draft', { id: draftId })
  }

  async function remove(draft: TDraft, origin: string = '') {
    spliceDraft(draft.id)
    decrementTypeTotals(draft.type)
    useProjectsStore().removeRecentItem('draft', draft.id)
    // Remove draft ID from thumbnail generation queue to prevent race condition
    dequeueThumbnail(draft.id)
    cancelPendingThumbnail([draft.id])
    const res = await deleteDraft(draft.id)
    if (!res) {
      track('draft-delete-failed', {
        name: draft.name,
        origin,
      })
      throw new Error(`Failed to delete project`)
    }
    track('draft-deleted', {
      name: draft.name,
      origin,
    })
  }

  async function removeDrafts(selectedDrafts: TDraft[]) {
    const ids = selectedDrafts.map((d) => d.id)
    // Remove draft IDs from thumbnail generation queue to prevent race condition
    ids.forEach(dequeueThumbnail)
    cancelPendingThumbnail(ids)
    const res = await http.post('/delete_drafts', { ids })
    if (!res) {
      track('drafts-delete-failed', {
        count: ids.length,
      })
      throw new Error(`Failed to delete docs`)
    }
    track('drafts-deleted', {
      count: ids.length,
    })
    ids.forEach(spliceDraft)
    selectedDrafts.forEach((selected) => decrementTypeTotals(selected.type))
  }

  async function mergeDrafts(drafts: TDraft[]): Promise<number> {
    const ids = drafts.map((d) => d.id)
    const result = await http.post<any>('/merge_drafts', { ids })
    if (result.status !== 'success') {
      track('drafts-merge-failed', {
        count: ids.length,
      })
      throw new Error('Error merging docs!')
    }
    track('drafts-merged', {
      count: ids.length,
    })
    return result.draftId
  }

  async function get(id: number, enhanced = false, signal?: AbortSignal) {
    if (id === -1 || !id) {
      throw new Error(`Draft ID is invalid: ${id}!`)
    }
    try {
      const res = await http.post<TDraft>('/get_draft', { id, enhanced }, false, 0, signal)
      draft.value = res
      draftFlags.value = await getFlags(id, signal)
      return res
    } catch (error) {
      if (signal?.aborted) throw error
      draft.value = null
    }
  }

  function getRouteForType(type: string): string {
    switch (type) {
      case 'Standard': {
        return `/docs/ebook`
      }
      case 'Audio':
      case 'TemiJob': {
        return `/docs/transcription`
      }
      case 'AudioBook': {
        return `/docs/audiobook`
      }
      case 'PrintBook': {
        return `/docs/print-book`
      }
      case 'KindleBook': {
        return `/docs/kindle-book`
      }
      default:
        throw new Error('[not handled] edit draft ' + type)
    }
  }

  async function goToEditDraft(id: number, type: string, from: string): Promise<void> {
    const route = getRouteForType(type)
    track('draft-edit', {
      type: type,
      from: from,
    })
    await router.push(`${route}/${id}`)
  }

  async function applyTags(draftId: number, tags: number[]): Promise<boolean> {
    try {
      const res = await http.post('/apply_tags_to_draft/' + draftId, { tags })

      if (!res) {
        throw new Error(`Failed to apply tags`)
      }

      return true
    } catch {
      return false
    }
  }

  async function getFlags(draftId: number, signal?: AbortSignal): Promise<TDraftFlags | null> {
    try {
      const res = await http.get<TDraftFlags>('/draft_flags/' + draftId, undefined, false, signal)

      if (!res) {
        return null
      }

      return res
    } catch (error) {
      if (signal?.aborted) throw error

      return null
    }
  }

  async function fetchSummary(draftId: number): Promise<string | null> {
    try {
      const res = await http.get<{ status: string; result: { summary: string } }>(`/ai/draft-summary/${draftId}`)
      if (res?.status === 'success' && res?.result?.summary) {
        return res.result.summary
      }
      return null
    } catch {
      return null
    }
  }

  async function detectLanguage(draftId: number, languages?: string[]): Promise<string | null> {
    try {
      const payload = languages?.length ? { languages } : undefined
      const res = await http.post<TDraftLanguageDetectionResponse>(`/drafts/${draftId}/language`, payload)

      if (!res?.result) {
        return null
      }

      return res.detected ?? null
    } catch {
      return null
    }
  }

  async function generatePrologue(draftId: number): Promise<string | null> {
    const snack = useSnackbarStore()
    try {
      const res = await http.get<{ status: string; message?: string; result: { prologue: string } }>(
        `/ai/draft-prologue/${draftId}`,
      )
      if (res?.status === 'success' && res?.result?.prologue) {
        return res.result.prologue
      }
      const msg =
        res?.message === 'NOT_ENOUGH_CREDITS'
          ? 'Oops! It looks like you have run out of credits. Please contact support for more free credits.'
          : 'There was a problem generating the prologue, please try again in a moment.'
      snack.add(msg, { permanent: res?.message === 'NOT_ENOUGH_CREDITS' })
      return null
    } catch {
      snack.add('There was a problem generating the prologue, please try again in a moment.')
      return null
    }
  }

  async function generateDescription(draftId: number, maxWords = 200): Promise<string | null> {
    const snack = useSnackbarStore()
    try {
      const res = await http.get<{ status: string; message?: string; result: { summary: string } }>(
        `/ai/draft-summary/${draftId}`,
      )
      if (res?.status === 'success' && res?.result?.summary) {
        const words = res.result.summary.trim().split(/\s+/)
        return words.slice(0, maxWords).join(' ')
      }
      const msg =
        res?.message === 'NOT_ENOUGH_CREDITS'
          ? 'Oops! It looks like you have run out of credits. Please contact support for more free credits.'
          : 'There was a problem generating the description, please try again in a moment.'
      snack.add(msg, { permanent: res?.message === 'NOT_ENOUGH_CREDITS' })
      return null
    } catch {
      snack.add('There was a problem generating the description, please try again in a moment.')
      return null
    }
  }

  async function setFlags(draftId: number, flags: TDraftFlags): Promise<boolean> {
    try {
      const payload = {
        id: draftId,
        flags,
      }

      await http.post('/draft_flags', payload)

      return true
    } catch {
      return false
    }
  }

  async function fetchTypeTotals(types: readonly TDraftTab[]): Promise<void> {
    await Promise.all(types.map(fetchTypeTotal))
  }

  async function fetchTypeTotal(type: TDraftTab): Promise<void> {
    // a listing fetch already in flight reports the same total, so waiting on it saves a request
    const pendingLoad = pendingLoads.get(type)
    if (pendingLoad) {
      return await pendingLoad
    }
    if (listing(type).loaded) {
      return
    }

    const pending = pendingTotals.get(type)
    if (pending) {
      return await pending
    }

    const load = (async () => {
      try {
        const res = await http.post<{ results: TDraft[]; pagination: TDraftsPagination }>(
          `/get_drafts/1/${type}/newest/1`,
          { query: '' },
        )
        if (res?.pagination) {
          typeTotals.value[type] = res.pagination.total
        }
      } catch (err: unknown) {
        console.warn(`[Drafts] fetchTypeTotals: failed to fetch count for ${type}`, err)
      }
    })().finally(() => {
      pendingTotals.delete(type)
    })

    pendingTotals.set(type, load)
    return await load
  }

  return {
    draft,
    typeTotals,
    typeTabs,
    draftsSortBy,
    storeUnsavedContent,
    getLabel,
    getTypeLabel,
    listing,
    createListing,
    ensure,
    search,
    setSort,
    invalidate,
    invalidateAll,
    reload,
    patchDraft,
    fetchNext,
    fetchTypeTotals,
    pollTranscriptionStatuses,
    isTranscriptionPollable,
    save,
    rename,
    duplicate,
    remove,
    removeDrafts,
    deleteDraft,
    get,
    mergeDrafts,
    goToEditDraft,
    applyTags,
    getFlags,
    setFlags,
    detectLanguage,
    fetchSummary,
    generateDescription,
    generatePrologue,
    draftFlags,
    isCurrentDraftPrintBook,
    linkCoverToKindleBookDraft,
    saveKindleBookSettings,
    isCurrentDraftKindleBook,
  }
})
